import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

@observer
class VippsCheckoutModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            error: null,
            paymentStatus: null,
            paymentComplete: false,
            paymentFailed: false,
        };
        this.pollTimer = null;
        this.sdkLoaded = false;
    }

    componentDidMount() {
        this.loadSdkAndCreateSession();
    }

    componentWillUnmount() {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    loadSdkAndCreateSession = async () => {
        try {
            // Load the Vipps Checkout SDK script if not already loaded
            if (!window.VippsCheckout) {
                await this.loadScript('https://checkout.vipps.no/vippsCheckoutSDK.js');
            }
            this.sdkLoaded = true;

            // Create checkout session via backend - unless the caller already has one
            // (guest checkout creates it server-side without a JWT and passes it in).
            const {
                stores, amount, title, type, customer, race, raceClassContestant, sale, convention, conventionParticipantId, session,
            } = this.props;
            const { vippsStore } = stores;

            let sessionData = session;
            if (!sessionData) {
                const response = await vippsStore.createCheckoutSession({
                    amount,
                    title,
                    type,
                    customer,
                    race,
                    raceClassContestant,
                    sale,
                    convention,
                    conventionParticipantId,
                    wantedPage: window.location.href,
                });

                if (response?.status !== 200 || !response?.data?.token) {
                    this.setState({
                        loading: false,
                        error: response?.message || 'Failed to create checkout session.',
                    });
                    return;
                }
                sessionData = response.data;
            }

            const { token, checkoutFrontendUrl, paymentUuidv4 } = sessionData;

            this.paymentUuidv4 = paymentUuidv4;
            this.checkoutFrontendUrl = checkoutFrontendUrl;
            this.checkoutToken = token;

            // Set loading false so the container div renders, then init checkout
            // after DOM updates
            this.setState({ loading: false }, () => {
                setTimeout(() => {
                    this.initCheckout(checkoutFrontendUrl, token);
                }, 100);
            });

            // Start polling for payment status
            this.startPolling(paymentUuidv4);
        } catch (err) {
            console.error('[VippsCheckoutModal] Error:', err);
            this.setState({
                loading: false,
                error: err.message || 'An error occurred.',
            });
        }
    }

    loadScript = (src) => {
        return new Promise((resolve, reject) => {
            // Check if already loaded
            const existing = document.querySelector(`script[src="${src}"]`);
            if (existing) {
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.onload = resolve;
            script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
            document.head.appendChild(script);
        });
    }

    initCheckout = (checkoutFrontendUrl, token) => {
        if (!window.VippsCheckout) {
            console.error('[VippsCheckoutModal] VippsCheckout SDK not loaded');
            return;
        }

        try {
            window.VippsCheckout({
                checkoutFrontendUrl,
                iFrameContainerId: 'vipps-checkout-frame-container',
                language: 'nb',
                token,
                on: {
                    shipping_option_selected: (data) => {
                        // Not used in PaymentOnly mode
                    },
                    session_status_changed: (data) => {
                        console.log('[VippsCheckoutModal] Session status changed:', data);
                        this.handleSessionStatusChange(data);
                    },
                    total_amount_changed: (data) => {
                        // Not used in PaymentOnly mode
                    },
                },
            });
        } catch (err) {
            console.error('[VippsCheckoutModal] Failed to initialize VippsCheckout:', err);
            this.setState({ error: 'Failed to initialize Vipps checkout.' });
        }
    }

    handleSessionStatusChange = (data) => {
        const status = data?.status || data;
        console.log('[VippsCheckoutModal] Status:', status);

        if (status === 'PaymentSuccessful' || status === 'AUTHORIZED' || status === 'CAPTURED') {
            this.setState({ paymentComplete: true });
            // Give the callback a moment to process, then confirm
            setTimeout(() => this.confirmPaymentAndClose(), 2000);
        } else if (status === 'PaymentTerminated' || status === 'SessionExpired' || status === 'TERMINATED' || status === 'EXPIRED') {
            this.setState({ paymentFailed: true });
            setTimeout(() => this.handleCancel(), 2000);
        }
    }

    startPolling = (uuidv4) => {
        const { stores, getStatus } = this.props;
        const { vippsStore } = stores;
        // Guest checkout polls the public status route via getStatus; everyone
        // else uses the JWT-protected store method.
        const poll = getStatus || (id => vippsStore.getCheckoutSessionStatus(id));

        this.pollTimer = setInterval(async () => {
            const response = await poll(uuidv4);
            if (response?.data?.paymentStatus >= 2 && response?.data?.paymentStatus < 90) {
                this.setState({ paymentStatus: response.data.paymentStatus, paymentComplete: true });
                clearInterval(this.pollTimer);
                this.pollTimer = null;
                this.confirmPaymentAndClose();
            } else if (response?.data?.paymentStatus >= 90) {
                this.setState({ paymentStatus: response.data.paymentStatus, paymentFailed: true });
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        }, 3000);
    }

    confirmPaymentAndClose = async () => {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }

        const { onComplete } = this.props;
        if (onComplete) {
            await onComplete({ paymentUuidv4: this.paymentUuidv4 });
        }
    }

    handleCancel = () => {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }

        const { onCancel } = this.props;
        if (onCancel) {
            onCancel();
        }
    }

    handleClose = () => {
        const { paymentComplete } = this.state;
        if (paymentComplete) {
            this.confirmPaymentAndClose();
        } else {
            this.handleCancel();
        }
    }

    render() {
        const { title, amount } = this.props;
        const { loading, error, paymentComplete, paymentFailed } = this.state;

        return (
            <div class='modal d-block' tabindex='-1' style='background-color: rgba(0,0,0,0.6); z-index: 1060;'>
                <div class='modal-dialog modal-lg modal-dialog-centered'>
                    <div class='modal-content'>
                        <div class='modal-header'>
                            <h5 class='modal-title'>
                                <img src='/assets/external/Vipps logo.png' style='width: 80px;' class='me-2' />
                                {title || 'Vipps Checkout'}
                            </h5>
                            <button type='button' class='btn-close' onClick={this.handleClose} />
                        </div>
                        <div class='modal-body' style='min-height: 400px;'>
                            {loading && (
                                <div class='d-flex flex-column align-items-center justify-content-center' style='min-height: 300px;'>
                                    <div class='spinner-border text-primary mb-3' role='status'>
                                        <span class='visually-hidden'>Loading...</span>
                                    </div>
                                    <p class='text-body-secondary'>
                                        <Text id='payment.vipps-checkout-loading'>Setting up Vipps Checkout...</Text>
                                    </p>
                                </div>
                            )}

                            {error && (
                                <div class='alert alert-danger'>
                                    <i class='fa-duotone fa-triangle-exclamation me-2' />
                                    {error}
                                </div>
                            )}

                            {paymentComplete && (
                                <div class='d-flex flex-column align-items-center justify-content-center' style='min-height: 300px;'>
                                    <div class='display-1 text-success mb-3'>
                                        <i class='fa-duotone fa-check fa-beat' />
                                    </div>
                                    <p class='fs-5'>
                                        <Text id='payment.vipps-payment-done'>Payment is done.</Text>
                                    </p>
                                </div>
                            )}

                            {paymentFailed && !paymentComplete && (
                                <div class='d-flex flex-column align-items-center justify-content-center' style='min-height: 300px;'>
                                    <div class='display-1 text-danger mb-3'>
                                        <i class='fa-duotone fa-xmark' />
                                    </div>
                                    <p class='fs-5'>
                                        <Text id='payment.vipps-payment-cancelled'>Payment was cancelled or expired.</Text>
                                    </p>
                                </div>
                            )}

                            {!loading && !error && !paymentComplete && !paymentFailed && (
                                <div id='vipps-checkout-frame-container' style='min-height: 400px;' />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default VippsCheckoutModal;
